import { useEffect, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { SpinProps, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';

import { displayErrorMessage } from 'components/message';
import Spin from 'components/Spin';
import Attachment from 'components/Attachment';
import {
  AdminListingApprovalDetail,
  ListingApi,
  ListingStatus,
} from 'services/api';
import { dateTimeFormat } from 'utils/date.utils';
import DataPlaceholder from '../DataPlaceholder';
import BuyerInfo from '../BuyerInfo';
import ContactList from '../ContactList';
import ApproveButton from './ApproveButton';

const getColumns = (
  t: TFunction,
  listingStatus?: ListingStatus,
): ColumnsType<AdminListingApprovalDetail> => [
  {
    key: 'date',
    title: t('BidManagement.Tabs.Table.Date'),
    render: (_, { created_at }) => (
      <span>{moment(created_at).format(dateTimeFormat)}</span>
    ),
  },
  {
    key: 'buyer',
    title: t('BidManagement.Tabs.Table.Buyer'),
    render: (_, { user, nickname, status, dre }) => (
      <>
        {user && (
          <BuyerInfo
            firstName={user.first_name}
            lastName={user.last_name}
            nickname={nickname}
            status={status}
            dre={dre}
          />
        )}
      </>
    ),
  },
  {
    key: 'contacts',
    title: t('BidManagement.Tabs.Table.Contacts'),
    render: (_, { user }) => <ContactList user={user} />,
  },
  {
    key: 'approval_docs',
    title: t('BidManagement.Tabs.Table.ApprovalInformation'),
    render: (_, { files }) => (
      <>
        {files?.map((file) => (
          // BE should describe model better in the future, but now it might work only that way
          // @ts-ignore
          <div key={file.url}>
            <Attachment
              // @ts-ignore
              downloadUrl={file.url}
              // @ts-ignore
              fileName={file.filename}
            />
          </div>
        ))}
      </>
    ),
  },
  {
    key: 'approve_button',
    width: '12%',
    render: (_, { listing_id, user }) => (
      <ApproveButton
        isDisabledButton={
          listingStatus === ListingStatus.ENDED ||
          listingStatus === ListingStatus.PENDING ||
          listingStatus === ListingStatus.SOLD ||
          listingStatus === ListingStatus.WITHDRAWN
        }
        listingId={listing_id}
        userId={user.id}
      />
    ),
  },
];
type Props = {
  isActive: boolean;
  listingId?: string;
  listingStatus?: ListingStatus;
};
export default function PendingRequests({
  isActive,
  listingId,
  listingStatus,
}: Props) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<AdminListingApprovalDetail[]>();
  const columns = useMemo(
    () => getColumns(t, listingStatus),
    [t, listingStatus],
  );
  useEffect(() => {
    if (!isActive || !listingId) return;

    const loadData = async () => {
      setIsLoading(true);

      try {
        const result = await ListingApi.getBidPendingRequests(listingId);

        setData(result);
      } catch (error) {
        displayErrorMessage({ error });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isActive, listingId]);

  const tableLoading: SpinProps = {
    spinning: isLoading,
    indicator: <Spin />,
  };

  return (
    <DataPlaceholder
      isDataExists={!!data && data.length > 0}
      isLoading={!data?.length && isLoading}
      noDataDescription={t('BidManagement.Tabs.PendingRequests.Empty')}
    >
      <Table
        scroll={{ x: '100%' }}
        dataSource={data}
        columns={columns}
        rowKey={(row) => row.user.id}
        loading={tableLoading}
        pagination={false}
      />
    </DataPlaceholder>
  );
}
